import type { GeoJSONSource, Map as MlMap, RasterSourceSpecification, RasterTileSource } from 'maplibre-gl';
import { LANES_LAYER } from './layers';
import { apiUrl } from '../api';

// --- Precipitation radar (RainViewer) ---------------------------------
export const PRECIP_SOURCE = 'weather-precip';
export const PRECIP_LAYER = 'weather-precip-layer';
const RAINVIEWER_INDEX_URL = 'https://api.rainviewer.com/public/weather-maps.json';
const PRECIP_TILE_SIZE = 256;
const PRECIP_COLOR_SCHEME = 2; // "Universal Blue" — reads fine on both light and projector-dark base maps
const PRECIP_SMOOTH = 1;
const PRECIP_SNOW = 1;

// --- METAR flight-category stations ------------------------------------
export const METAR_SOURCE = 'weather-metars';
export const METAR_LAYER = 'weather-metars-layer';
export const METAR_LABELS_LAYER = 'weather-metars-labels-layer';
const METAR_LABEL_MINZOOM = 6;
const METAR_URL = apiUrl('/api/metar');

interface RainviewerFrame {
  time: number;
  path: string;
}
interface RainviewerResponse {
  host: string;
  radar?: { past?: RainviewerFrame[]; nowcast?: RainviewerFrame[] };
}

interface MetarRaw {
  icaoId?: string;
  rawOb?: string;
  lat?: number;
  lon?: number;
  wdir?: number | string;
  wspd?: number;
  wgst?: number;
  fltCat?: 'VFR' | 'MVFR' | 'IFR' | 'LIFR' | string;
}

/**
 * Registers the precip raster layer and METAR circle/symbol layers, both
 * hidden by default. Precip is inserted with `beforeId: LANES_LAYER` so it
 * renders underneath every app data layer (LANES_LAYER is the bottom-most
 * one already — see layers.ts). Safe to call once at map setup, mirroring
 * setupLayers' idempotent `if (!map.getLayer(...))` guards.
 */
export function addWeatherLayers(map: MlMap, opts: { projector: boolean }): void {
  if (!map.getSource(PRECIP_SOURCE)) {
    const spec: RasterSourceSpecification = {
      type: 'raster',
      // Placeholder tile template until the first refreshPrecip() resolves a
      // real frame path; RainViewer tiles 404 harmlessly until then.
      tiles: [`https://tilecache.rainviewer.com/v2/radar/0/${PRECIP_TILE_SIZE}/{z}/{x}/{y}/${PRECIP_COLOR_SCHEME}/${PRECIP_SMOOTH}_${PRECIP_SNOW}.png`],
      tileSize: PRECIP_TILE_SIZE,
      // RainViewer serves radar only to ~zoom 7; cap here so MapLibre overzooms
      // (scales) those tiles instead of requesting unsupported higher zooms,
      // which return opaque "Zoom Level Not Supported" placeholder tiles that
      // veil the whole map.
      maxzoom: 7,
      attribution: 'RainViewer',
    };
    map.addSource(PRECIP_SOURCE, spec);
  }
  if (!map.getLayer(PRECIP_LAYER)) {
    map.addLayer(
      {
        id: PRECIP_LAYER,
        type: 'raster',
        source: PRECIP_SOURCE,
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': opts.projector ? 0.7 : 0.6,
          'raster-opacity-transition': { duration: 220 },
        },
      },
      map.getLayer(LANES_LAYER) ? LANES_LAYER : undefined,
    );
  }

  if (!map.getSource(METAR_SOURCE)) {
    map.addSource(METAR_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(METAR_LAYER)) {
    map.addLayer({
      id: METAR_LAYER,
      type: 'circle',
      source: METAR_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'circle-radius': 5,
        'circle-color': [
          'match',
          ['get', 'fltCat'],
          'VFR',
          opts.projector ? '#22ff88' : '#16a34a',
          'MVFR',
          opts.projector ? '#38bdf8' : '#0284c7',
          'IFR',
          opts.projector ? '#ff3b3b' : '#dc2626',
          'LIFR',
          opts.projector ? '#ff5cff' : '#c026d3',
          /* unknown */ opts.projector ? '#94a3b8' : '#64748b',
        ],
        'circle-opacity': 0.9,
        'circle-stroke-width': 1.2,
        'circle-stroke-color': opts.projector ? '#000000' : '#ffffff',
      },
    });
    map.addLayer({
      id: METAR_LABELS_LAYER,
      type: 'symbol',
      source: METAR_SOURCE,
      minzoom: METAR_LABEL_MINZOOM,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': opts.projector ? 12 : 10,
        'text-offset': [0, 1],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': opts.projector ? '#e2e8f0' : '#1e293b',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
      },
    });
  }
}

/**
 * Fetch RainViewer's frame index and point the precip raster source at the
 * latest past frame's tile path. RainViewer rotates frames every ~10min;
 * re-calling this is cheap (one small JSON fetch) so callers can poll it on
 * whatever cadence they like. Fetch/parse failures are swallowed — the old
 * frame (if any) just keeps rendering.
 */
export async function refreshPrecip(map: MlMap): Promise<void> {
  try {
    const res = await fetch(RAINVIEWER_INDEX_URL);
    if (!res.ok) return;
    const data = (await res.json()) as RainviewerResponse;
    const past = data.radar?.past;
    if (!past || past.length === 0) return;
    const latest = past[past.length - 1];
    const src = map.getSource(PRECIP_SOURCE) as (RasterTileSource & { setTiles?: (tiles: string[]) => void }) | undefined;
    if (!src || typeof src.setTiles !== 'function') return;
    const tiles = [
      `${data.host}${latest.path}/${PRECIP_TILE_SIZE}/{z}/{x}/{y}/${PRECIP_COLOR_SCHEME}/${PRECIP_SMOOTH}_${PRECIP_SNOW}.png`,
    ];
    src.setTiles(tiles);
  } catch {
    /* offline or malformed response — keep whatever frame is already loaded */
  }
}

export function setPrecipVisible(map: MlMap, visible: boolean): void {
  if (!map.getLayer(PRECIP_LAYER)) return;
  map.setLayoutProperty(PRECIP_LAYER, 'visibility', visible ? 'visible' : 'none');
}

export function setMetarsVisible(map: MlMap, visible: boolean): void {
  const vis: 'visible' | 'none' = visible ? 'visible' : 'none';
  if (map.getLayer(METAR_LAYER)) map.setLayoutProperty(METAR_LAYER, 'visibility', vis);
  if (map.getLayer(METAR_LABELS_LAYER)) map.setLayoutProperty(METAR_LABELS_LAYER, 'visibility', vis);
}

function formatWind(wdir: number | string | undefined, wspd: number | undefined, wgst: number | undefined): string {
  if (wspd == null) return '';
  const dir = typeof wdir === 'number' ? String(wdir).padStart(3, '0') : 'VRB';
  const gust = wgst ? `G${wgst}` : '';
  return `${dir}@${wspd}${gust}`;
}

/**
 * Load METARs for `bbox` from the server proxy (aviationweather.gov doesn't
 * send Access-Control-Allow-Origin for direct browser fetches — verified via
 * curl) and render one circle+label feature per station, colored by flight
 * category. Raw METAR text is kept in `properties.rawOb` for a future popup.
 */
export async function refreshMetars(
  map: MlMap,
  bbox: { lamin: number; lomin: number; lamax: number; lomax: number },
): Promise<void> {
  const src = map.getSource(METAR_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  try {
    const url = `${METAR_URL}?lamin=${bbox.lamin}&lomin=${bbox.lomin}&lamax=${bbox.lamax}&lomax=${bbox.lomax}`;
    const res = await fetch(url);
    if (!res.ok) return;
    const stations = (await res.json()) as MetarRaw[];
    const features: GeoJSON.Feature[] = [];
    for (const s of stations) {
      if (s.lat == null || s.lon == null || !s.icaoId) continue;
      const wind = formatWind(s.wdir, s.wspd, s.wgst);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
        properties: {
          station: s.icaoId,
          fltCat: s.fltCat ?? 'unknown',
          rawOb: s.rawOb ?? '',
          label: wind ? `${s.icaoId} ${wind}` : s.icaoId,
        },
      });
    }
    src.setData({ type: 'FeatureCollection', features });
  } catch {
    /* offline or malformed response — leave previous stations in place */
  }
}
